import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ViewerActions } from '@app/client/viewer/actions';
import * as fromRoot from '@app/reducers';
import { fadeInOutAnimation } from '@app/shared/animations/fadeinout-animations';
import { SmashPlayerComponent } from '@app/shared/modules/smash-player/smash-player/smash-player.component';
import { Device } from '@app/shared/models/device';
import { Preview } from '@app/shared/models/preview';
import { TransferFile } from '@app/shared/models/transferFile';
import { Store } from '@ngrx/store';
import { SwiperConfigInterface } from 'ngx-swiper-wrapper';
import { Options, SourceInfo } from 'plyr';
import * as fromViewer from '../../reducers/viewer.reducer';

@Component({
  selector: 'app-modal-preview',
  templateUrl: './modal-preview.component.html',
  styleUrls: ['./modal-preview.component.scss'],
  animations: [
    fadeInOutAnimation
  ]
})
export class ModalPreviewComponent implements OnInit, OnChanges {

  @Input() preview: Preview;
  @Input() file: TransferFile;
  @Input() files: TransferFile[];
  @Input() device: Device;

  config: SwiperConfigInterface = {}
  selectedFile: TransferFile;
  soundPlayerDisplayed = false;
  playerSource: SourceInfo;
  playerOptions: Options;

  filesReady = false;

  @ViewChild('player') player: SmashPlayerComponent;

  constructor(
    private store$: Store<fromRoot.State & fromViewer.State>,
  ) { }

  ngOnInit(): void {
    this.buildSwipperConfig();
    this.setSelectedFile(this.file);
    this.loadMoreFilesIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.files) {
      if (this.selectedFile) {
        this.selectedFile = this.files.find(file => file.index === this.selectedFile.index);
        if (this.selectedFile.type === 'audio' || this.selectedFile.type === 'video') {
          this.setupPlayer();
        };
      }
    }
  }

  buildSwipperConfig(): void {
    this.config = {
      a11y: true,
      direction: 'horizontal',
      slidesPerView: 1,
      keyboard: true,
      mousewheel: true,
      scrollbar: false,
      pagination: false,
      spaceBetween: 10,
      navigation: this.device.isDesktop,
      allowTouchMove: this.device.isMobile || this.device.isTablet,
      observer: true,
      preloadImages: false,
      lazy: true
    };
  }

  /* Loading more files when opening preview modal on last loaded files (slider event 'onReachEnd' not triggered on init) */
  loadMoreFilesIfNeeded(): void {
    if (this.file.index + 1 === this.files.length && this.files.length < this.preview.transfer.filesNumber) {
      this.store$.dispatch(ViewerActions.LoadMoreFiles());
    }
  }

  downloadFile(file: TransferFile): void {
    this.store$.dispatch(ViewerActions.RequestDownloadUrls({ file }));
  }

  onReachEnd(): void {
    if (this.files.length < this.preview.transfer.filesNumber) {
      this.store$.dispatch(ViewerActions.LoadMoreFiles());
    }
  }

  closeModal(): void {
    this.store$.dispatch(ViewerActions.CloseModalPreview());
  }

  setSelectedFile(file: TransferFile): void {
    this.selectedFile = file;
    if (this.selectedFile) {
      if (this.selectedFile.type === 'audio' || this.selectedFile.type === 'video') {
        this.setupPlayer();
      }
    }
  }

  onIndexChange({ index }: { index: number }) {
    this.selectedFile = this.files.find(file => file.index === index);
    if (Date.parse(this.selectedFile.expiry) - Date.now() < 0) {
      this.store$.dispatch(ViewerActions.RefreshFileAndAssociatedFiles({ file: this.selectedFile }));
    } else {
      if (this.selectedFile.type === 'audio' || this.selectedFile.type === 'video') {
        this.setupPlayer();
      }
    }
  }

  showSoundPlayer(): void {
    this.soundPlayerDisplayed = true;
  }

  lockNavigation(): void {
    this.config.navigation = false;
    this.config.allowTouchMove = false;
  }

  unlockNavigation(): void {
    this.config.navigation = true;
    this.config.allowTouchMove = this.device.isMobile || this.device.isTablet;
  }

  // Section for handling players //

  setupPlayer(): void {
    this.playerSource = this.buildPlayerSource(this.selectedFile);
    this.playerOptions = this.buildPlayerOptions(this.selectedFile);
  }

  get playerIsPlaying(): boolean {
    if (!!this.player) {
      return this.player.isPlaying;
    }
    return false;
  }

  togglePlay(): void {
    if (this.player) {
      if (this.player.isPlaying) {
        this.player.pause();
      } else {
        this.player.play();
      }
    }
  }

  buildPlayerSource(file: TransferFile): SourceInfo {
    const sources: SourceInfo = {
      sources: [
        {
          src: file.preview,
          provider: file.provider as Plyr.Provider,
        }
      ],
      type: file.type as Plyr.MediaType,
    }
    return sources;
  }

  buildPlayerOptions(file: TransferFile): Options {
    const options: Options = {
      volume: 100,
      disableContextMenu: true,
      storage: { enabled: false }
    };

    if (file.type === 'audio') {
      options.controls = ['progress', 'duration'];
      options.autoplay = false;
    }

    if (file.type === 'video') {
      options.controls = ['play', 'progress', 'volume', 'speed', 'duration', 'mute', 'settings', 'fullscreen'];
      options.fullscreen = { enabled: true, fallback: true, iosNative: this.device.deviceInfo.os === 'iOS' || this.device.deviceInfo.os === 'Mac', container: null }
    }

    return options;
  }

}
